import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MdArrowForward, MdArrowBack, MdTableChart, MdCheckCircle, MdPlace, MdPerson, MdEmail, MdInfo, MdAdd, MdEdit, MdClose } from 'react-icons/md';
import { buildCampaignPropertySubject } from '../utils/campaignSubject';
import '../styles/ColumnMappingStep.css';

/**
 * Known canonical fields - columns matching these don't need descriptions
 */
const KNOWN_CANONICAL_KEYS = new Set([
  'property_address', 'city', 'property_name', 'leasing_company', 'leasing_contact', 'email',
  'total_sf', 'rent_sf_yr', 'ops_ex_sf', 'gross_rent', 'drive_ins', 'docks', 'ceiling_ht', 'power',
  'listing_comments', 'flyer_link', 'floorplan', 'client_comments'
]);

/**
 * Default required fields (can be toggled by user)
 */
const DEFAULT_REQUIRED_FIELDS = new Set([
  'total_sf', 'rent_sf_yr', 'ops_ex_sf', 'drive_ins', 'docks', 'ceiling_ht', 'power'
]);

const DEFAULT_FORMULA_HEADER_FALLBACKS = {
  total_sf: 'Total SF',
  rent_sf_yr: 'Rent/SF /Yr',
  ops_ex_sf: 'Ops Ex /SF'
};

function formulaSourceHeader(columns, canonicalKey) {
  return columns.find(col => col.canonicalKey === canonicalKey)?.header
    || DEFAULT_FORMULA_HEADER_FALLBACKS[canonicalKey];
}

function buildDefaultFormulaExpression(canonicalKey, columns) {
  if (canonicalKey !== 'gross_rent') return '';

  const totalSf = formulaSourceHeader(columns, 'total_sf');
  const rent = formulaSourceHeader(columns, 'rent_sf_yr');
  const opsEx = formulaSourceHeader(columns, 'ops_ex_sf');
  return `({${rent}} + {${opsEx}}) * {${totalSf}} / 12`;
}

function formulaBlankInputs(expression, columns, outputHeader) {
  const availableHeaders = new Set(columns.map(col => col.header));
  const blankInputs = [];
  const seen = new Set();
  const tokenPattern = /\{([^{}]+)\}/g;
  let match;

  while ((match = tokenPattern.exec(expression)) !== null) {
    const header = match[1].trim();
    if (header === outputHeader || !availableHeaders.has(header) || seen.has(header)) continue;
    seen.add(header);
    blankInputs.push(header);
  }

  return blankInputs;
}

function buildInitialFormulaExpressions(columns) {
  const expressions = {};
  columns.forEach(col => {
    if (!col.isFormula || !col.canonicalKey) return;
    expressions[col.index] = buildDefaultFormulaExpression(col.canonicalKey, columns)
      || col.formulaInfo?.formula
      || '';
  });
  return expressions;
}

/**
 * ColumnMappingStep - Three-page column review UI
 *
 * Page 1: Name your campaign (+ see existing clients)
 * Page 2: Verify extracted properties & emails
 * Page 3: Configure data columns (Mode + Required toggle)
 */
export default function ColumnMappingStep({
  columns = [],
  onConfirm,
  onBack,
  onCancel,
  scriptMentionedFields = [],
  extractedData = null,
  validationIssues = [],
  clientName = '',
  suggestedName = '',
  existingClientNames = [],
  savedDraft = null,
  onDraftChange,
}) {
  // R14 1.7: Back from the outreach step remounts this component. Without the
  // draft the user came back to page 1 with every column setting reset to its
  // default; seeded with it they return to the page they left, with their own
  // choices intact.
  const [page, setPage] = useState(() => savedDraft?.page || 1);

  // Editable client name
  const [editedClientName, setEditedClientName] = useState(
    savedDraft?.editedClientName !== undefined ? savedDraft.editedClientName : clientName);

  // The campaign name arrives pre-filled from the uploaded workbook, so it is a
  // suggestion rather than something the user typed. Select it once, on arrival,
  // so the first keystroke replaces it instead of landing after it. Without this
  // the caret sits at the end of the suggestion and typing appends to it.
  const campaignNameInputRef = useRef(null);
  const suggestionOfferedRef = useRef(false);
  // Only an explicitly pending workbook suggestion is offered. The parent
  // clears `suggestedName` when this step is accepted, including when the user
  // accepts it unchanged, so a later remount cannot select an accepted name.
  const nameSuggestion = suggestedName;

  useEffect(() => {
    if (suggestionOfferedRef.current) return;
    if (page !== 1) return;
    suggestionOfferedRef.current = true;
    const input = campaignNameInputRef.current;
    if (!input || !nameSuggestion || input.value !== nameSuggestion) return;
    input.select();
  }, [page, nameSuggestion]);

  // Editable emails state (for missing emails)
  const [editedEmails, setEditedEmails] = useState(() => savedDraft?.editedEmails || {});

  // Track which rows are enabled for the campaign (by row id)
  const [enabledRows, setEnabledRows] = useState(() => {
    if (savedDraft?.enabledRowIds) return new Set(savedDraft.enabledRowIds);
    if (!extractedData?.allProperties) return new Set();
    const validRows = new Set();
    extractedData.allProperties.forEach((p, i) => {
      if (p.email && p.email.includes('@')) {
        validRows.add(i);
      }
    });
    return validRows;
  });

  // Column modes: 'ask', 'note', 'skip'
  const [columnModes, setColumnModes] = useState(() => {
    if (savedDraft?.columnModes) return savedDraft.columnModes;
    const modes = {};
    columns.forEach(col => {
      if (col.category === 'identification') return;

      if (col.isFormula) {
        modes[col.index] = 'skip';
      } else {
        const defaultMode = col.defaultMode || 'off';
        const scriptUpgrade = scriptMentionedFields.includes(col.canonicalKey) &&
          (defaultMode === 'track' || defaultMode === 'off');

        if (scriptUpgrade || defaultMode === 'gather') {
          modes[col.index] = 'ask';
        } else if (defaultMode === 'track') {
          modes[col.index] = 'note';
        } else {
          modes[col.index] = 'skip';
        }
      }
    });
    return modes;
  });

  // Required toggles: which columns are required for row completion
  const [requiredToggles, setRequiredToggles] = useState(() => {
    if (savedDraft?.requiredToggles) return savedDraft.requiredToggles;
    const required = {};
    columns.forEach(col => {
      if (col.category === 'identification') return;
      // Default: check if it's in the default required set
      required[col.index] = DEFAULT_REQUIRED_FIELDS.has(col.canonicalKey);
    });
    return required;
  });

  // Custom field descriptions (for non-canonical columns marked as Ask)
  const [customDescriptions, setCustomDescriptions] = useState(() => savedDraft?.customDescriptions || {});
  // Track which description inputs are expanded (hidden by default)
  const [expandedDescriptions, setExpandedDescriptions] = useState(() => savedDraft?.expandedDescriptions || {});
  const [formulaExpressions, setFormulaExpressions] = useState(
    () => savedDraft?.formulaExpressions || buildInitialFormulaExpressions(columns));

  // Report the draft upward so a remount (Back from the outreach step) can be
  // seeded with it. The parent holds it in a ref, so this never re-renders here.
  const draftChangeRef = useRef(onDraftChange);
  draftChangeRef.current = onDraftChange;
  useEffect(() => {
    draftChangeRef.current?.({
      page,
      editedClientName,
      editedEmails,
      enabledRowIds: [...enabledRows],
      columnModes,
      requiredToggles,
      customDescriptions,
      expandedDescriptions,
      formulaExpressions,
    });
  }, [page, editedClientName, editedEmails, enabledRows, columnModes, requiredToggles,
      customDescriptions, expandedDescriptions, formulaExpressions]);

  // Separate identification vs data columns
  const dataCols = useMemo(() => {
    return columns.filter(col => col.category !== 'identification');
  }, [columns]);

  const formulaCols = useMemo(() => {
    return dataCols.filter(col => col.isFormula && col.canonicalKey);
  }, [dataCols]);

  // Extract property rows for display
  const extractedRows = useMemo(() => {
    if (!extractedData) return [];
    const { allProperties } = extractedData;
    return (allProperties || []).map((p, index) => ({
      id: index,
      address: p.address || '',
      city: p.city || '',
      // R14 1.2: three suites at one street read as the same property here
      // unless the suite label the upload carries comes with them.
      propertyName: p.propertyName || '',
      contact: p.contact || '',
      email: p.email || '',
      rowIndex: p.rowIndex
    }));
  }, [extractedData]);

  // Get the effective email for a row
  const getRowEmail = (row) => {
    return editedEmails[row.id] !== undefined ? editedEmails[row.id] : row.email;
  };

  // Toggle row enabled/disabled
  const toggleRow = useCallback((rowId) => {
    setEnabledRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  // Only consider enabled rows for validation
  const enabledExtractedRows = useMemo(() => {
    return extractedRows.filter(row => enabledRows.has(row.id));
  }, [extractedRows, enabledRows]);

  // Check if all ENABLED rows have valid emails
  const allEmailsValid = useMemo(() => {
    if (enabledExtractedRows.length === 0) return false;
    return enabledExtractedRows.every(row => {
      const email = editedEmails[row.id] !== undefined ? editedEmails[row.id] : row.email;
      return email && email.includes('@');
    });
  }, [enabledExtractedRows, editedEmails]);

  // Count missing emails in ENABLED rows only
  const missingEmailCount = useMemo(() => {
    return enabledExtractedRows.filter(row => {
      const email = editedEmails[row.id] !== undefined ? editedEmails[row.id] : row.email;
      return !email || !email.includes('@');
    }).length;
  }, [enabledExtractedRows, editedEmails]);

  // Client name validation (for page 1)
  const clientNameTrimmed = editedClientName.trim();
  const clientNameValid = clientNameTrimmed.length > 0;
  const clientNameDuplicate = existingClientNames.includes(clientNameTrimmed);

  const setMode = useCallback((colIndex, mode) => {
    setColumnModes(prev => ({ ...prev, [colIndex]: mode }));
  }, []);

  const toggleRequired = useCallback((colIndex) => {
    setRequiredToggles(prev => ({ ...prev, [colIndex]: !prev[colIndex] }));
  }, []);

  const setDescription = useCallback((colIndex, desc) => {
    setCustomDescriptions(prev => ({ ...prev, [colIndex]: desc }));
  }, []);

  // Check if a column needs a custom description
  const needsDescription = useCallback((col) => {
    const mode = columnModes[col.index];
    const isCustom = !KNOWN_CANONICAL_KEYS.has(col.canonicalKey);
    return isCustom && mode === 'ask';
  }, [columnModes]);

  // Build final config on confirm
  const handleConfirm = useCallback(() => {
    const mappings = {};
    const extractionFields = [];
    const requiredFields = [];
    const formulaFields = [];
    const formulaDefinitions = [];
    const neverRequest = [];
    const customFields = {};

    columns.forEach(col => {
      const mode = columnModes[col.index];
      const isRequired = requiredToggles[col.index];

      // Handle identification columns
      if (col.category === 'identification') {
        if (col.canonicalKey) {
          mappings[col.canonicalKey] = col.header;
        }
        return;
      }

      if (col.isFormula) {
        if (col.canonicalKey) {
          mappings[col.canonicalKey] = col.header;
          formulaFields.push(col.canonicalKey);

          const expression = (formulaExpressions[col.index] || '').trim();
          if (expression) {
            formulaDefinitions.push({
              id: col.canonicalKey,
              label: col.header,
              outputColumn: col.header,
              expression,
              blankIfAny: formulaBlankInputs(expression, columns, col.header),
              enabled: true
            });
          }
        }
        return;
      }

      // Skip skipped columns
      if (mode === 'skip') return;

      // Check if this is a known canonical field or custom
      const isKnownCanonical = KNOWN_CANONICAL_KEYS.has(col.canonicalKey);

      if (isKnownCanonical) {
        // Known field - add to mappings
        mappings[col.canonicalKey] = col.header;

        if (mode === 'ask') {
          extractionFields.push(col.canonicalKey);
          if (isRequired && !col.isFormula) {
            requiredFields.push(col.canonicalKey);
          }
        }
        if (mode === 'note') {
          extractionFields.push(col.canonicalKey);
          neverRequest.push(col.canonicalKey);
        }
      } else {
        // Custom field - add to customFields
        const description = customDescriptions[col.index] || `Extract value for ${col.header}`;
        customFields[col.header] = {
          mode: isRequired ? 'ask_required' : (mode === 'ask' ? 'ask_optional' : mode),
          description: description,
        };
      }
    });

    onConfirm({
      mappings,
      extractionFields,
      requiredFields,
      formulaFields,
      formulaDefinitions,
      neverRequest,
      customFields,
      editedEmails,
      enabledRowIds: Array.from(enabledRows),
      clientName: editedClientName.trim(),
    });
  }, [columns, columnModes, requiredToggles, customDescriptions, formulaExpressions, onConfirm, editedEmails, enabledRows, editedClientName]);

  // Stats for page 2
  const askCount = dataCols.filter(c => columnModes[c.index] === 'ask').length;
  const noteCount = dataCols.filter(c => columnModes[c.index] === 'note').length;
  const requiredCount = dataCols.filter(c => columnModes[c.index] === 'ask' && requiredToggles[c.index]).length;

  // Check if any custom fields need descriptions
  const customFieldsMissingDesc = dataCols.filter(col => {
    if (!needsDescription(col)) return false;
    const desc = customDescriptions[col.index] || '';
    return desc.trim().length === 0;
  });

  // ===== PAGE 1: Name Your Campaign =====
  if (page === 1) {
    return (
      <div className="column-mapping-step">
        <div className="mapping-header">
          <div className="mapping-header-icon">
            <MdAdd />
          </div>
          <div className="mapping-header-text">
            <h3>Name Your Campaign</h3>
          </div>
        </div>

        {/* Campaign name input - centered and prominent */}
        <div className="campaign-name-hero">
          <div className="campaign-name-input-wrapper">
            <MdEdit className="campaign-name-pencil" />
            <input
              type="text"
              className={`campaign-name-input ${!clientNameValid ? 'campaign-name-input--empty' : ''} ${clientNameDuplicate ? 'campaign-name-input--error' : ''}`}
              placeholder="Enter campaign name..."
              value={editedClientName}
              onChange={(e) => setEditedClientName(e.target.value)}
              ref={campaignNameInputRef}
              autoFocus
            />
          </div>
          {clientNameDuplicate ? (
            <span className="campaign-name-error">This name already exists</span>
          ) : (
            <span className="campaign-name-hint">This will appear on your dashboard.</span>
          )}
        </div>

        {/* Visual: Existing clients with new one joining */}
        {existingClientNames.length > 0 && (
          <div className="existing-clients-preview">
            <div className="existing-clients-list">
              {existingClientNames.slice(0, 4).map((name, i) => (
                <div key={i} className="existing-client-chip">
                  {name}
                </div>
              ))}
              {existingClientNames.length > 4 && (
                <div className="existing-client-chip existing-client-chip--more">
                  +{existingClientNames.length - 4} more
                </div>
              )}
              {/* New campaign joining */}
              {clientNameValid && !clientNameDuplicate && (
                <div className="existing-client-chip existing-client-chip--new">
                  <MdAdd className="new-icon" />
                  {editedClientName}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mapping-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-confirm"
            onClick={() => setPage(2)}
            disabled={!clientNameValid || clientNameDuplicate}
            title={!clientNameValid ? 'Enter a campaign name' : clientNameDuplicate ? 'Name already exists' : ''}
          >
            Proceed
            <MdArrowForward />
          </button>
        </div>
      </div>
    );
  }

  // ===== PAGE 2: Verify Properties =====
  if (page === 2) {
    return (
      <div className="column-mapping-step">
        <div className="mapping-header">
          <div className="mapping-header-icon">
            <MdCheckCircle />
          </div>
          <div className="mapping-header-text">
            <h3>Verify Properties</h3>
            <p className="mapping-subtitle">
              We found {extractedRows.length} properties. Confirm emails and select which to include.
            </p>
          </div>
        </div>

        {/* Selection hint */}
        <div className="selection-hint">
          <span className="selection-hint-text">
            Uncheck any properties you want to exclude from this campaign
          </span>
          <button
            type="button"
            className="selection-toggle-btn"
            onClick={() => {
              if (enabledRows.size === extractedRows.length) {
                setEnabledRows(new Set());
              } else {
                setEnabledRows(new Set(extractedRows.map(r => r.id)));
              }
            }}
          >
            {enabledRows.size === extractedRows.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Table header */}
        <div className="extracted-table-header">
          <div className="extracted-col extracted-col--checkbox">
            <input
              type="checkbox"
              className="extracted-checkbox extracted-checkbox--header"
              checked={enabledRows.size === extractedRows.length && extractedRows.length > 0}
              onChange={() => {
                if (enabledRows.size === extractedRows.length) {
                  setEnabledRows(new Set());
                } else {
                  setEnabledRows(new Set(extractedRows.map(r => r.id)));
                }
              }}
              title={enabledRows.size === extractedRows.length ? 'Deselect all' : 'Select all'}
            />
          </div>
          <div className="extracted-col extracted-col--property">
            <MdPlace className="extracted-col-icon" />
            Property
          </div>
          <div className="extracted-col extracted-col--contact">
            <MdPerson className="extracted-col-icon" />
            Contact
          </div>
          <div className="extracted-col extracted-col--email">
            <MdEmail className="extracted-col-icon" />
            Email
          </div>
        </div>

        {/* Table rows */}
        <div className="extracted-table-body">
          {extractedRows.map((row) => {
            const currentEmail = getRowEmail(row);
            const emailValid = currentEmail && currentEmail.includes('@');
            const isEnabled = enabledRows.has(row.id);

            return (
              <div
                key={row.id}
                className={`extracted-row ${!isEnabled ? 'extracted-row--disabled' : ''} ${isEnabled && !emailValid ? 'extracted-row--missing' : ''}`}
              >
                <div className="extracted-cell extracted-cell--checkbox">
                  <input
                    type="checkbox"
                    className="extracted-checkbox"
                    checked={isEnabled}
                    onChange={() => toggleRow(row.id)}
                    title={isEnabled ? 'Exclude from campaign' : 'Include in campaign'}
                  />
                </div>
                <div className="extracted-cell extracted-cell--property">
                  {buildCampaignPropertySubject(row)
                    || `${row.address}${row.city ? `, ${row.city}` : ''}`}
                </div>
                <div className="extracted-cell extracted-cell--contact">
                  {row.contact || <span className="extracted-cell-empty">—</span>}
                </div>
                <div className="extracted-cell extracted-cell--email">
                  <input
                    type="email"
                    className={`extracted-email-input ${emailValid ? 'extracted-email-input--valid' : ''} ${!isEnabled ? 'extracted-email-input--disabled-row' : ''}`}
                    placeholder={isEnabled ? "Enter email..." : "Enter email to include..."}
                    value={editedEmails[row.id] !== undefined ? editedEmails[row.id] : row.email}
                    onChange={(e) => {
                      if (!isEnabled && e.target.value.length > 0) {
                        setEnabledRows(prev => new Set([...prev, row.id]));
                      }
                      setEditedEmails(prev => ({
                        ...prev,
                        [row.id]: e.target.value
                      }));
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Validation issues */}
        {validationIssues.length > 0 && (
          <div className="validation-issues">
            {validationIssues.map((issue, i) => (
              <div key={i} className={`validation-issue validation-issue--${issue.type}`}>
                {issue.message}
              </div>
            ))}
          </div>
        )}

        <div className="extracted-summary">
          {enabledExtractedRows.length === 0 ? (
            <span className="extracted-summary--warning">No properties selected</span>
          ) : missingEmailCount > 0 ? (
            <span className="extracted-summary--warning">
              <strong>{missingEmailCount}</strong> selected {missingEmailCount === 1 ? 'property needs' : 'properties need'} an email address
            </span>
          ) : (
            <>
              <strong>{enabledExtractedRows.length}</strong> of {extractedRows.length} {extractedRows.length === 1 ? 'property' : 'properties'} selected
            </>
          )}
        </div>

        <div className="mapping-actions">
          <button type="button" className="btn-back" onClick={() => setPage(1)}>
            <MdArrowBack />
            Back
          </button>
          <button
            type="button"
            className="btn-confirm"
            onClick={() => setPage(3)}
            disabled={enabledExtractedRows.length === 0 || !allEmailsValid}
            title={enabledExtractedRows.length === 0 ? 'Select at least one property' : !allEmailsValid ? 'Fix email issues above' : ''}
          >
            {enabledExtractedRows.length === 1 ? '1 Property' : `${enabledExtractedRows.length} Properties`}
            <MdArrowForward />
          </button>
        </div>
      </div>
    );
  }

  // ===== PAGE 3: Column Configuration =====
  return (
    <div className="column-mapping-step">
      <div className="mapping-header">
        <div className="mapping-header-icon">
          <MdTableChart />
        </div>
        <div className="mapping-header-text">
          <h3>Configure Data Columns</h3>
          <p className="mapping-subtitle">
            Choose how the AI should handle each column when emailing brokers.
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="mode-legend">
        <div className="mode-legend-item mode-legend-item--ask">
          <span className="mode-legend-label">Ask</span>
          <span className="mode-legend-desc">AI will request this info from brokers</span>
        </div>
        <div className="mode-legend-item mode-legend-item--note">
          <span className="mode-legend-label">Note</span>
          <span className="mode-legend-desc">Capture if mentioned, but don't ask</span>
        </div>
        <div className="mode-legend-item mode-legend-item--skip">
          <span className="mode-legend-label">Skip</span>
          <span className="mode-legend-desc">Ignore this column entirely</span>
        </div>
      </div>

      {/* Column table header */}
      <div className="column-config-header">
        <div className="column-config-header-name">Column</div>
        <div className="column-config-header-mode">Mode</div>
        <div className="column-config-header-required" title="Fields required before AI sends closing email">
          Required for Close
        </div>
      </div>

      {/* Column list */}
      <div className="column-config-list">
        {dataCols.map(col => {
          const currentMode = columnModes[col.index] || 'skip';
          const isRequired = requiredToggles[col.index] || false;
          const isCustom = !KNOWN_CANONICAL_KEYS.has(col.canonicalKey);
          const showDescription = needsDescription(col);
          const canBeRequired = currentMode === 'ask' && !col.isFormula;

          return (
            <div key={col.index} className="column-config-row-wrapper">
              <div className={`column-config-row ${col.isFormula ? 'column-config-row--formula' : ''} ${isCustom ? 'column-config-row--custom' : ''}`}>
                <div className="column-config-name">
                  {col.header}
                  {col.isFormula && <span className="formula-badge">Formula</span>}
                  {isCustom && !col.isFormula && <span className="custom-badge">Custom</span>}
                </div>
                <div className="column-config-modes">
                  <button
                    type="button"
                    className={`mode-btn mode-btn--ask ${currentMode === 'ask' ? 'mode-btn--active' : ''}`}
                    onClick={() => setMode(col.index, 'ask')}
                    disabled={col.isFormula}
                  >
                    Ask
                  </button>
                  <button
                    type="button"
                    className={`mode-btn mode-btn--note ${currentMode === 'note' ? 'mode-btn--active' : ''}`}
                    onClick={() => setMode(col.index, 'note')}
                  >
                    Note
                  </button>
                  <button
                    type="button"
                    className={`mode-btn mode-btn--skip ${currentMode === 'skip' ? 'mode-btn--active' : ''}`}
                    onClick={() => setMode(col.index, 'skip')}
                  >
                    Skip
                  </button>
                </div>
                <div className="column-config-required">
                  <input
                    type="checkbox"
                    className="required-checkbox"
                    checked={isRequired && canBeRequired}
                    onChange={() => toggleRequired(col.index)}
                    disabled={!canBeRequired}
                    title={canBeRequired ? (isRequired ? 'Required: AI won\'t close until this is filled' : 'Optional: AI will close even if empty') : 'Only "Ask" columns can be required for close'}
                  />
                </div>
              </div>

              {/* Description toggle for custom columns */}
              {showDescription && (
                <div className="column-config-description">
                  {expandedDescriptions[col.index] || customDescriptions[col.index] ? (
                    <div className="description-input-row">
                      <input
                        type="text"
                        className="description-input"
                        placeholder={`e.g., "Number of ${col.header.toLowerCase().replace(/s$/, '')} spots"`}
                        value={customDescriptions[col.index] || ''}
                        onChange={(e) => setDescription(col.index, e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="description-collapse-btn"
                        onClick={() => setExpandedDescriptions(prev => ({ ...prev, [col.index]: false }))}
                        title="Hide hint"
                      >
                        <MdClose size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="description-add-btn"
                      onClick={() => setExpandedDescriptions(prev => ({ ...prev, [col.index]: true }))}
                      title="Add context to help the AI understand this field"
                    >
                      <MdInfo size={14} />
                      <span>Add Context (Recommended)</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {formulaCols.length > 0 && (
        <div className="formula-config-panel">
          <div className="formula-config-header">
            <span className="formula-config-title">Calculated Columns</span>
            <span className="formula-config-note">Use column names inside braces.</span>
          </div>
          {formulaCols.map(col => {
            const inputId = `formula-expression-${col.index}`;
            return (
              <label key={col.index} className="formula-config-row" htmlFor={inputId}>
                <span className="formula-config-label">{col.header} formula</span>
                <input
                  id={inputId}
                  type="text"
                  className="formula-config-input"
                  value={formulaExpressions[col.index] || ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setFormulaExpressions(prev => ({ ...prev, [col.index]: value }));
                  }}
                  placeholder="Example: ({Rent/SF /Yr} + {Ops Ex /SF}) * {Total SF} / 12"
                />
              </label>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="mapping-summary">
        <span className="summary-stat"><strong>{askCount}</strong> to ask</span>
        <span className="summary-divider">·</span>
        <span className="summary-stat" title="AI won't send closing email until these are filled"><strong>{requiredCount}</strong> required for close</span>
        <span className="summary-divider">·</span>
        <span className="summary-stat"><strong>{noteCount}</strong> to note</span>
      </div>

      {/* Info for custom fields without descriptions */}
      {customFieldsMissingDesc.length > 0 && (
        <div className="custom-fields-info">
          <MdInfo />
          <span>
            {customFieldsMissingDesc.length === 1
              ? `Recommended: Add context for "${customFieldsMissingDesc[0].header}" to help the AI`
              : `Recommended: Add context for ${customFieldsMissingDesc.length} custom columns to help the AI`
            }
          </span>
        </div>
      )}

      <div className="mapping-actions">
        <button type="button" className="btn-back" onClick={() => setPage(2)}>
          <MdArrowBack />
          Back
        </button>
        <button
          type="button"
          className="btn-confirm"
          onClick={handleConfirm}
        >
          Continue
          <MdArrowForward />
        </button>
      </div>
    </div>
  );
}
